import React, { useState, useRef, useEffect } from 'react';
import './AdvancedSelect.scss';
import { Search } from '@livingdesign/icons';
import { Button, Spinner } from '@livingdesign/react';

export const QA_ID_PREFIX = "AdvancedSelect";

function AdvancedSelect({
    options = [],
    value = null,
    onChange = () => {},
    placeholder = "Select...",
    isMulti = false,
    searchable = true,
    disabled = false,
    loading = false,
    isServerSideSearch = false,
    onSearch = null,
    searchDebounceMs = 300,
    className = "",
    maxDisplayedChips = 3,
    noOptionsMessage = "No options available",
    searchPlaceholder = "Search options...",
    showApplyButton = false
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [isDropup, setIsDropup] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isUserTyping, setIsUserTyping] = useState(false);
    const [tempSelectedValues, setTempSelectedValues] = useState([]);
    const dropdownRef = useRef(null);
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    useEffect(() => {
        if (isServerSideSearch) {
            setFilteredOptions(options);
        } else {
            setFilteredOptions(
                options.filter(option =>
                    option.label.toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
        }
    }, [options, isServerSideSearch]);

    useEffect(() => {
        if (!isServerSideSearch && searchTerm) {
            setFilteredOptions(
                options.filter(option =>
                    option.label.toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
        }
    }, [searchTerm, options, isServerSideSearch]);

    useEffect(() => {
        if (!isServerSideSearch) return;

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (!searchTerm.trim()) {
            setIsSearching(false);
            setIsUserTyping(false);
            if (onSearch && isUserTyping) {
                onSearch('');
            }
            return;
        }

        if (!onSearch || !isUserTyping) return;

        setIsSearching(true);

        searchTimeoutRef.current = setTimeout(async () => {
            try {
                console.log('Making server-side search call for:', searchTerm.trim());
                await onSearch(searchTerm.trim());
            } catch (error) {
                console.error('Search error:', error);
            } finally {
                setIsSearching(false);
                setIsUserTyping(false);
            }
        }, searchDebounceMs);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm, isServerSideSearch, onSearch, searchDebounceMs, isUserTyping]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
                setIsUserTyping(false);
                setIsDropup(false);
            }
        };

        const handleResize = () => {
            if (isOpen) {
                calculateDropdownPosition();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleResize);
        };
    }, [isOpen]);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            calculateDropdownPosition();
        }
    }, [filteredOptions, isOpen, isMulti]);

    useEffect(() => {
        if (isOpen && searchable && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen, searchable]);

    const calculateDropdownPosition = () => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        
        const estimatedDropdownHeight = Math.min(300, 
            (searchable ? 48 : 0) +
            (isMulti && filteredOptions.length > 0 ? 40 : 0) +
            (filteredOptions.length * 40) +
            20
        );

        const spaceBelow = viewportHeight - containerRect.bottom;
        const spaceAbove = containerRect.top;

        const shouldDropup = spaceBelow < estimatedDropdownHeight && spaceAbove > estimatedDropdownHeight;
        setIsDropup(shouldDropup);
    };

    const handleToggleDropdown = () => {
        if (!disabled && !loading) {
            if (!isOpen) {
                setIsOpen(true);
                setSearchTerm('');
                setIsUserTyping(false);
                // Initialize temp values when opening with showApplyButton
                if (showApplyButton && isMulti) {
                    setTempSelectedValues(Array.isArray(value) ? [...value] : []);
                }
                setTimeout(calculateDropdownPosition, 0);
            } else {
                setIsOpen(false);
                setSearchTerm('');
                setIsUserTyping(false);
                setIsDropup(false);
                // Reset temp values when closing without applying
                if (showApplyButton && isMulti) {
                    setTempSelectedValues([]);
                }
            }
        }
    };

    const handleSelectAll = () => {
        if (isMulti) {
            const currentValues = showApplyButton ? tempSelectedValues : (Array.isArray(value) ? value : []);
            const allSelected = filteredOptions.every(option => 
                currentValues.some(v => v.value === option.value)
            );
            
            if (allSelected) {
                const newValues = currentValues.filter(selected => 
                    !filteredOptions.some(option => option.value === selected.value)
                );
                if (showApplyButton) {
                    setTempSelectedValues(newValues);
                } else {
                    onChange(newValues);
                }
            } else {
                const optionsToAdd = filteredOptions.filter(option => 
                    !currentValues.some(v => v.value === option.value)
                );
                if (showApplyButton) {
                    setTempSelectedValues([...currentValues, ...optionsToAdd]);
                } else {
                    onChange([...currentValues, ...optionsToAdd]);
                }
            }
        }
    };

    const isAllSelected = () => {
        if (!isMulti || filteredOptions.length === 0) return false;
        const currentValues = showApplyButton ? tempSelectedValues : (Array.isArray(value) ? value : []);
        return filteredOptions.every(option => 
            currentValues.some(v => v.value === option.value)
        );
    };

    const isSomeSelected = () => {
        if (!isMulti || filteredOptions.length === 0) return false;
        const currentValues = showApplyButton ? tempSelectedValues : (Array.isArray(value) ? value : []);
        return filteredOptions.some(option => 
            currentValues.some(v => v.value === option.value)
        ) && !isAllSelected();
    };

    const handleOptionClick = (option) => {
        if (isMulti) {
            const currentValues = showApplyButton ? tempSelectedValues : (Array.isArray(value) ? value : []);
            const isSelected = currentValues.some(v => v.value === option.value);
            
            if (isSelected) {
                const newValues = currentValues.filter(v => v.value !== option.value);
                if (showApplyButton) {
                    setTempSelectedValues(newValues);
                } else {
                    onChange(newValues);
                }
            } else {
                const newValues = [...currentValues, option];
                if (showApplyButton) {
                    setTempSelectedValues(newValues);
                } else {
                    onChange(newValues);
                }
            }
        } else {
            onChange(option);
            setIsOpen(false);
            setSearchTerm('');
            setIsUserTyping(false);
        }
    };

    const handleRemoveChip = (optionToRemove, event) => {
        event.stopPropagation();
        if (isMulti && Array.isArray(value)) {
            const newValues = value.filter(v => v.value !== optionToRemove.value);
            onChange(newValues);
        }
    };

    const isOptionSelected = (option) => {
        if (isMulti) {
            const currentValues = showApplyButton ? tempSelectedValues : value;
            return Array.isArray(currentValues) && currentValues.some(v => v.value === option.value);
        }
        return value && value.value === option.value;
    };

    const handleApply = () => {
        onChange(tempSelectedValues);
        setIsOpen(false);
        setSearchTerm('');
        setIsUserTyping(false);
    };

    const handleCancel = () => {
        setTempSelectedValues(Array.isArray(value) ? [...value] : []);
        setIsOpen(false);
        setSearchTerm('');
        setIsUserTyping(false);
    };

    const renderDisplayValue = () => {
        if (isMulti) {
            const selectedValues = Array.isArray(value) ? value : [];
            if (selectedValues.length === 0) {
                return <span className="placeholder" data-testid={`${QA_ID_PREFIX}-placeholder`}>{placeholder}</span>;
            }
            
            const displayedValues = selectedValues.slice(0, maxDisplayedChips);
            const remainingCount = selectedValues.length - maxDisplayedChips;
            
            return (
                <div className="multi-value-container" data-testid={`${QA_ID_PREFIX}-multi-value-container`}>
                    {displayedValues.map((selectedValue, index) => (
                        <span key={selectedValue.value} className="chip" data-testid={`${QA_ID_PREFIX}-chip-${index}`}>
                            {selectedValue.label}
                        </span>
                    ))}
                    {remainingCount > 0 && (
                        <span className="chip remaining-count" data-testid={`${QA_ID_PREFIX}-chip-remaining`}>
                            +{remainingCount} more
                        </span>
                    )}
                </div>
            );
        }
        
        return value ? (
            <span className="single-value" data-testid={`${QA_ID_PREFIX}-single-value`}>{value.label}</span>
        ) : (
            <span className="placeholder" data-testid={`${QA_ID_PREFIX}-placeholder`}>{placeholder}</span>
        );
    };

    const renderSelectedChips = () => {
        if (!isMulti || !Array.isArray(value) || value.length === 0) {
            return null;
        }

        return (
            <div className="selected-chips" data-testid={`${QA_ID_PREFIX}-selected-chips`}>
                {value.map((selectedValue, index) => (
                    <span key={selectedValue.value} className='chip' title={selectedValue.label} data-testid={`${QA_ID_PREFIX}-selected-chip-${index}`}>
                        <span style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '150px',
                            display: 'inline-block'
                        }}>
                            {selectedValue.label}
                        </span>
                        <button
                            type="button"
                            className="chip-remove"
                            onClick={(e) => handleRemoveChip(selectedValue, e)}
                            aria-label={`Remove ${selectedValue.label}`}
                            data-testid={`${QA_ID_PREFIX}-chip-remove-${index}`}
                        >
                            ×
                        </button>
                    </span>
                ))}
            </div>
        );
    };

    return (
        <div 
            className={`attribute-value-select ${className} ${disabled || loading ? 'disabled' : ''} ${isOpen ? 'open' : ''} ${isDropup ? 'dropup' : ''} ${loading ? 'loading' : ''}`}
            ref={containerRef}
            data-testid={`${QA_ID_PREFIX}-container`}
        >
            <div 
                className="select-control"
                onClick={handleToggleDropdown}
                tabIndex={disabled || loading ? -1 : 0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggleDropdown();
                    }
                }}
                role="combobox"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                data-testid={`${QA_ID_PREFIX}-select-control`}
            >
                <div className="select-value" data-testid={`${QA_ID_PREFIX}-select-value`}>
                    {renderDisplayValue()}
                </div>
                <div className="select-indicators" data-testid={`${QA_ID_PREFIX}-select-indicators`}>
                    {loading ? (
                        <span className="loading-spinner" data-testid={`${QA_ID_PREFIX}-loading-spinner`}>
                            <Spinner size="small" />
                        </span>
                    ) : (
                        <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`} data-testid={`${QA_ID_PREFIX}-dropdown-arrow`}>
                            ▼
                        </span>
                    )}
                </div>
            </div>

            {isOpen && (
                <div className={`select-menu ${isDropup ? 'dropup' : ''}`} ref={dropdownRef} data-testid={`${QA_ID_PREFIX}-select-menu`}>
                    {searchable && (
                        <div className="search-container" data-testid={`${QA_ID_PREFIX}-search-container`}>
                            <span className="search-icon" data-testid={`${QA_ID_PREFIX}-search-icon`}>
                                {isServerSideSearch && isSearching ? (
                                    <Spinner size='small' />
                                ) : (
                                    <Search size='small' />
                                )}
                            </span>
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="search-input"
                                placeholder={searchPlaceholder}
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setIsUserTyping(true);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`${QA_ID_PREFIX}-search-input`}
                            />
                        </div>
                    )}
                    
                    {isMulti && filteredOptions.length > 0 && (
                        <div className="select-all-container" data-testid={`${QA_ID_PREFIX}-select-all-container`}>
                            <div
                                className={`select-all-option ${isAllSelected() ? 'all-selected' : isSomeSelected() ? 'some-selected' : ''}`}
                                onClick={handleSelectAll}
                                role="option"
                                aria-selected={isAllSelected()}
                                data-testid={`${QA_ID_PREFIX}-select-all-option`}
                            >
                                <input
                                    type="checkbox"
                                    checked={isAllSelected()}
                                    ref={(input) => {
                                        if (input) input.indeterminate = isSomeSelected();
                                    }}
                                    onChange={() => {}} // Handled by onClick
                                    className="option-checkbox"
                                    data-testid={`${QA_ID_PREFIX}-select-all-checkbox`}
                                />
                                <span className="option-label" data-testid={`${QA_ID_PREFIX}-select-all-label`}>
                                    Select All ({filteredOptions.length})
                                </span>
                            </div>
                        </div>
                    )}
                    
                    <div className="options-container" data-testid={`${QA_ID_PREFIX}-options-container`}>
                        {filteredOptions.length === 0 ? (
                            <div className="no-options" data-testid={`${QA_ID_PREFIX}-no-options`}>
                                {searchTerm ? `No options found for "${searchTerm}"` : noOptionsMessage}
                            </div>
                        ) : (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={option.value}
                                    className={`option ${isOptionSelected(option) ? 'selected' : ''}`}
                                    onClick={() => handleOptionClick(option)}
                                    role="option"
                                    aria-selected={isOptionSelected(option)}
                                    data-testid={`${QA_ID_PREFIX}-option-${index}`}
                                >
                                    {isMulti && (
                                        <input
                                            type="checkbox"
                                            checked={isOptionSelected(option)}
                                            onChange={() => {}} // Handled by onClick
                                            className="option-checkbox"
                                            data-testid={`${QA_ID_PREFIX}-option-checkbox-${index}`}
                                        />
                                    )}
                                    <span className="option-label" data-testid={`${QA_ID_PREFIX}-option-label-${index}`}>{option.label}</span>
                                </div>
                            ))
                        )}
                    </div>
                    
                    {isMulti && showApplyButton && (
                        <div className="apply-cancel-buttons-container" data-testid={`${QA_ID_PREFIX}-apply-cancel-buttons`}>
                            <Button
                                variant="secondary"
                                onClick={handleCancel}
                                data-testid={`${QA_ID_PREFIX}-cancel-button`}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleApply}
                                data-testid={`${QA_ID_PREFIX}-apply-button`}
                            >
                                Apply
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {renderSelectedChips()}
        </div>
    );
}

export default AdvancedSelect;
